import type { EstimateAssumption, EstimateExclusion, EstimateWarning, EstimatorInput } from "../../types/index.js";
import { getScopeMultiplier } from "../scope-multipliers.js";
import {
  applyItem,
  findItem,
  scaleAppliedItem,
  type AppliedLineItem,
  type ProjectCalculationOutput,
  type ProjectCalculatorFn
} from "./shared.js";

/**
 * The Whole-Home Renovation Estimator (Phase 10).
 *
 * Per the Phase 10 brief: "Do not calculate the entire project using only
 * one square-foot rate... support base whole-home renovation pricing
 * combined with major scope selections... prevent double counting where
 * individual room calculations and whole-home allowances overlap."
 *
 * How this calculator satisfies that:
 *
 *  1. ONE anchor line item, `whole_home_base_renovation`, is priced per
 *     total home square footage. Per its own pricing-data doc comment
 *     (packages/pricing-data/data/sample-line-items-v2.ts), that item is
 *     scoped, by construction, to cover ONLY general shell/finish labour
 *     across the home (drywall repair, paint, trim, interior doors,
 *     general site overhead) - it never represents flooring, kitchen,
 *     bathrooms, electrical, plumbing, HVAC, windows/doors, exterior
 *     work, or structural changes.
 *  2. Every one of those excluded categories is its own, independently
 *     gated and independently priced line item below - each only applies
 *     when the corresponding answer says it's in scope. Because the base
 *     rate's scope and every add-on's scope are mutually exclusive BY
 *     DEFINITION (not by a deduction heuristic that could drift or be
 *     bypassed), the same square footage or room can never be priced
 *     twice - see `assumptions` below, which states this explicitly so
 *     it's visible on every result rather than left as an implicit
 *     design detail. This is a deliberately more robust choice than
 *     estimating a kitchen/bathroom sqft "carve-out" from the total and
 *     subtracting it from the base-rate quantity: a carve-out heuristic
 *     is itself a guess, and a bug in it would silently under- or
 *     over-count square footage. Mutually exclusive scope needs no such
 *     guess.
 *
 * Complexity factors from the brief that materially affect a whole-home
 * estimate and have no equivalent in a single-room project type:
 *  - Occupied vs. vacant (`whole_home_occupancy_status`): an occupied or
 *    partially-occupied home costs more to renovate (dust containment,
 *    protecting finished areas, phased access) - represented as a
 *    multiplier on the base renovation line, not a separate line item,
 *    since it's a cost-of-doing-business factor across the whole general
 *    scope rather than its own discrete scope of work.
 *  - Phased work (`whole_home_phasing`): phasing the project over time
 *    costs more overall than doing it all at once (repeated
 *    mobilization, temporary protection between phases) - same
 *    mechanism, a multiplier on the base line.
 *  - Major systems (electrical/plumbing/HVAC) and structural changes are
 *    represented as their own tiered/percentage line items below, per
 *    the "prevent double counting" section above.
 *  - Design services and permit requirements reuse the same
 *    percentage-of-base pattern kitchen/basement use for demolition, but
 *    scaled to whole-home project size (see `whole_home_permit_design`'s
 *    pricing-data doc comment for why this is a percentage here, unlike
 *    the flat allowance used for a single room).
 *
 * Occupancy and phasing multipliers are also factored into the
 * recommended contingency percentage - see contingency.ts.
 */

const FLOORS_COUNT_BY_ID: Record<string, number> = {
  "1": 1,
  "1_5": 1.5,
  "2": 2,
  "3_plus": 3
};

const BEDROOMS_COUNT_BY_ID: Record<string, number> = {
  "1": 1,
  "2": 2,
  "3": 3,
  "4": 4,
  "5_plus": 5
};

const BATHROOMS_COUNT_BY_ID: Record<string, number> = {
  "1": 1,
  "1_5": 1.5,
  "2": 2,
  "2_5": 2.5,
  "3": 3,
  "3_5": 3.5,
  "4_plus": 4
};

/**
 * Occupied/phased-work multipliers applied to the base renovation line
 * only - see the file-level doc comment for why these are multipliers on
 * the base line rather than their own line items. Kept as simple,
 * transparent, and multiplicative (not additive) planning-level factors,
 * consistent with the rest of this engine's approach to complexity - not
 * derived from any Canadian pricing dataset.
 */
const OCCUPANCY_MULTIPLIERS: Record<string, number> = {
  vacant: 1.0,
  partially_occupied: 1.08,
  occupied: 1.15
};

const PHASING_MULTIPLIERS: Record<string, number> = {
  single_phase: 1.0,
  phased: 1.06
};

const WINDOWS_DOORS_ITEM_BY_SCOPE: Record<string, string | null> = {
  none: null,
  some_replacement: "whole_home_windows_doors_partial",
  full_replacement: "whole_home_windows_doors_full"
};

const ELECTRICAL_ITEM_BY_TIER: Record<string, string> = {
  minor_updates: "whole_home_electrical_minor",
  partial_upgrade: "whole_home_electrical_partial",
  full_rewire_panel_upgrade: "whole_home_electrical_full"
};

const PLUMBING_ITEM_BY_TIER: Record<string, string> = {
  minor_updates: "whole_home_plumbing_minor",
  partial_repipe: "whole_home_plumbing_partial",
  full_repipe: "whole_home_plumbing_full"
};

const HVAC_ITEM_BY_SCOPE: Record<string, string | null> = {
  keep_existing: null,
  upgrade_existing: "whole_home_hvac_upgrade",
  new_system: "whole_home_hvac_new"
};

const EXTERIOR_ITEM_BY_SCOPE: Record<string, string | null> = {
  none: null,
  minor: "whole_home_exterior_minor",
  major: "whole_home_exterior_major"
};

function rawString(input: EstimatorInput, key: string): string | undefined {
  const value = input.rawAnswers[key];
  return typeof value === "string" ? value : undefined;
}

function rawBoolean(input: EstimatorInput, key: string): boolean {
  return input.rawAnswers[key] === true;
}

export const calculateWholeHome: ProjectCalculatorFn = (input, catalog, regionalMultiplier) => {
  const applied: AppliedLineItem[] = [];
  const assumptions: EstimateAssumption[] = [];
  const exclusions: EstimateExclusion[] = [];
  const warnings: EstimateWarning[] = [];
  let anchorMissing = false;

  const finishLevel = input.finishLevel;
  const sqft = input.roomSize?.value;

  const floorsId = rawString(input, "whole_home_floors");
  const floorsCount = floorsId ? FLOORS_COUNT_BY_ID[floorsId] : undefined;
  const bedroomsId = rawString(input, "whole_home_bedrooms");
  const bedroomsCount = bedroomsId ? BEDROOMS_COUNT_BY_ID[bedroomsId] : undefined;
  const bathroomsId = rawString(input, "whole_home_bathrooms");
  const bathroomsCount = bathroomsId ? BATHROOMS_COUNT_BY_ID[bathroomsId] : undefined;

  // --- Base whole-home renovation (anchor) + scope adjustment -------------
  // Always explicitly documented as excluding every category priced
  // separately below - this is the literal statement of the
  // double-counting-prevention design described in the file-level doc
  // comment, and is unit tested directly.
  let baseTypicalForPercentage = 0;
  if (sqft && sqft > 0) {
    const base = applyItem(catalog, "whole_home_base_renovation", { quantity: sqft, finishLevel, regionalMultiplier });
    if (base) {
      const scopeMultiplier = getScopeMultiplier(input.scope);

      const occupancyStatus = rawString(input, "whole_home_occupancy_status") ?? "vacant";
      const occupancyMultiplier = OCCUPANCY_MULTIPLIERS[occupancyStatus] ?? 1.0;
      const phasing = rawString(input, "whole_home_phasing") ?? "single_phase";
      const phasingMultiplier = PHASING_MULTIPLIERS[phasing] ?? 1.0;
      const combinedMultiplier = scopeMultiplier * occupancyMultiplier * phasingMultiplier;

      const scoped = scaleAppliedItem(base, combinedMultiplier);
      applied.push(scoped);
      baseTypicalForPercentage = scoped.result.typical?.amount ?? 0;

      assumptions.push({
        code: "scope_multiplier_applied",
        message: `Base renovation cost adjusted by a ${scopeMultiplier}x scope multiplier for "${input.scope}".`
      });
      if (occupancyMultiplier !== 1.0) {
        assumptions.push({
          code: "whole_home_occupancy_multiplier_applied",
          message:
            occupancyStatus === "occupied"
              ? `Base renovation cost increased by ${Math.round((occupancyMultiplier - 1) * 100)}% because the home will be occupied throughout construction (dust containment, protecting finished areas, and phased access all add cost).`
              : `Base renovation cost increased by ${Math.round((occupancyMultiplier - 1) * 100)}% because the home will be partially occupied during construction.`
        });
      }
      if (phasingMultiplier !== 1.0) {
        assumptions.push({
          code: "whole_home_phasing_multiplier_applied",
          message: `Base renovation cost increased by ${Math.round((phasingMultiplier - 1) * 100)}% because the work will be phased over time rather than done all at once (repeated mobilization and temporary protection between phases add cost).`
        });
        warnings.push({
          code: "whole_home_phased_timeline_notice",
          message: "Phased work extends the overall project timeline and typically requires re-budgeting between phases as material/labour costs shift - treat this estimate as a starting point for the whole project, not a fixed phase-by-phase budget.",
          severity: "caution"
        });
      }

      assumptions.push({
        code: "whole_home_double_counting_prevention",
        message:
          "The base whole-home renovation rate covers only general shell and finish work (drywall, paint, trim, interior doors, general site overhead) across the home's total square footage. Kitchen, bathrooms, basement, flooring, electrical, plumbing, HVAC, windows/doors, exterior work, and structural changes are each priced separately below and are not included in the base rate - this is what prevents the same square footage from being priced twice."
      });
    }
  } else {
    anchorMissing = true;
  }

  // --- Kitchen (only if included - see double-counting note above) --------
  if (rawBoolean(input, "whole_home_kitchen_included")) {
    const kitchen = applyItem(catalog, "whole_home_kitchen_allowance", { finishLevel, regionalMultiplier });
    if (kitchen) {
      applied.push(kitchen);
      // The kitchen allowance's scope is deliberately cabinetry/countertops/
      // appliances/finishes only - it does NOT include kitchen electrical or
      // plumbing rough-in. That work is priced separately via the whole-home
      // electrical/plumbing scope categories below (which apply home-wide),
      // so a kitchen that needs new circuits or moved plumbing is never
      // billed for that work in both the kitchen allowance and the
      // electrical/plumbing line item at once.
      assumptions.push({
        code: "whole_home_kitchen_allowance_scope",
        message:
          "The kitchen allowance covers cabinetry, countertops, appliances, and finishes only. Any electrical or plumbing work the kitchen needs is covered by the electrical/plumbing scope selections above (if selected), not by the kitchen allowance itself."
      });
    }
  } else {
    exclusions.push({ code: "excludes_whole_home_kitchen", message: "Kitchen was not indicated as included in this renovation - excluded from this estimate." });
  }

  // --- Basement (only if included) -----------------------------------------
  if (rawBoolean(input, "whole_home_basement_included")) {
    if (sqft && sqft > 0) {
      // Only total home square footage is captured, not basement square
      // footage specifically - approximated as one floor's footprint
      // (total sqft / number of floors), the same planning-level
      // heuristic pattern used elsewhere in this engine (e.g. kitchen's
      // derived cabinetry linear footage). Floors defaults to 1 (the most
      // conservative - largest - footprint estimate) when not answered.
      const floorsForBasement = floorsCount && floorsCount > 0 ? floorsCount : 1;
      const basementSqft = Math.max(400, Math.round(sqft / floorsForBasement));
      const basement = applyItem(catalog, "whole_home_basement_allowance", { quantity: basementSqft, finishLevel, regionalMultiplier });
      if (basement) {
        applied.push(basement);
        assumptions.push({
          code: "whole_home_basement_sqft_derived",
          message: `Basement square footage (${basementSqft} sqft) was estimated as total home square footage (${sqft} sqft) divided by the number of floors${floorsCount ? "" : " (assumed 1, since number of floors wasn't provided)"} - only total home square footage is captured as an input.`
        });
      }
    } else {
      exclusions.push({ code: "excludes_whole_home_basement_sizing", message: "Basement was indicated as included, but total home square footage was not provided, so basement square footage could not be estimated." });
    }
  } else {
    exclusions.push({ code: "excludes_whole_home_basement", message: "Basement was not indicated as included in this renovation - excluded from this estimate." });
  }

  // --- Bathrooms (priced per bathroom, always relevant to a whole-home project) ---
  if (bathroomsCount && bathroomsCount > 0) {
    const bathroomQuantity = Math.max(1, Math.round(bathroomsCount));
    const bathrooms = applyItem(catalog, "whole_home_bathroom_allowance", { quantity: bathroomQuantity, finishLevel, regionalMultiplier });
    if (bathrooms) {
      applied.push(bathrooms);
      if (bathroomQuantity !== bathroomsCount) {
        assumptions.push({
          code: "whole_home_bathroom_count_rounded",
          message: `${bathroomsCount} bathrooms was rounded to ${bathroomQuantity} for per-bathroom pricing purposes.`
        });
      }
    }
  } else {
    exclusions.push({ code: "excludes_whole_home_bathrooms", message: "Number of bathrooms was not provided - bathroom renovation cost is excluded from this estimate." });
  }

  // --- Windows / doors (tiered, per total sqft as an opening-count proxy) --
  const windowsDoorsScope = rawString(input, "whole_home_windows_doors_scope");
  const windowsDoorsItemId = windowsDoorsScope ? WINDOWS_DOORS_ITEM_BY_SCOPE[windowsDoorsScope] : undefined;
  if (windowsDoorsItemId && sqft && sqft > 0) {
    const windowsDoors = applyItem(catalog, windowsDoorsItemId, { quantity: sqft, finishLevel, regionalMultiplier });
    if (windowsDoors) applied.push(windowsDoors);
  } else if (windowsDoorsItemId) {
    // A scope was selected, but total home square footage is missing, so
    // the sqft-based proxy quantity can't be computed - the exclusion
    // reason is the missing square footage, not an absence of scope.
    exclusions.push({
      code: "excludes_whole_home_windows_doors",
      message: "Window/door changes were requested, but total home square footage was not provided, so this cost could not be estimated."
    });
  } else {
    exclusions.push({
      code: "excludes_whole_home_windows_doors",
      message: windowsDoorsScope
        ? "No window/door changes were requested - excluded from this estimate."
        : "No window/door selection was provided - excluded from this estimate."
    });
  }

  // --- Flooring (tiered quantity: full sqft for full replacement, roughly
  //     half for partial - see pricing-data doc comment) ------------------
  const flooringScope = rawString(input, "whole_home_flooring_scope");
  if (flooringScope === "full_replacement" || flooringScope === "partial_replacement") {
    if (sqft && sqft > 0) {
      const flooringQuantity = flooringScope === "full_replacement" ? sqft : Math.round(sqft * 0.5);
      const flooring = applyItem(catalog, "whole_home_flooring", { quantity: flooringQuantity, finishLevel, regionalMultiplier });
      if (flooring) {
        applied.push(flooring);
        if (flooringScope === "partial_replacement") {
          assumptions.push({
            code: "whole_home_partial_flooring_area_derived",
            message: `Partial flooring replacement was assumed to cover roughly half of the home's total square footage (${flooringQuantity} of ${sqft} sqft), since a specific area wasn't captured.`
          });
        }
      }
    }
  } else {
    exclusions.push({
      code: "excludes_whole_home_flooring",
      message: flooringScope === "keep_existing" ? "Existing flooring is being kept - no flooring cost is included." : "No flooring selection was provided - excluded from this estimate."
    });
  }

  // --- Electrical (gated on common electrical_changes, tiered) ------------
  const electricalTier = rawString(input, "whole_home_electrical_scope");
  if (input.workType.electricalChanges === "yes" || electricalTier) {
    const electricalItemId = electricalTier ? ELECTRICAL_ITEM_BY_TIER[electricalTier] : "whole_home_electrical_minor";
    const electrical = electricalItemId ? applyItem(catalog, electricalItemId, { finishLevel, regionalMultiplier }) : null;
    if (electrical) {
      applied.push(electrical);
      if (!electricalTier) {
        assumptions.push({ code: "whole_home_electrical_defaulted", message: 'Electrical changes were indicated but no scope tier was selected - defaulted to "minor updates" for planning purposes.' });
      }
    } else if (input.workType.electricalChanges === "yes") {
      warnings.push({ code: "whole_home_electrical_no_applicable_pricing", message: "Electrical changes were indicated, but no applicable electrical pricing could be applied for this configuration - consult an electrician directly for this scope.", severity: "caution" });
    }
  } else {
    exclusions.push({ code: "excludes_whole_home_electrical", message: "Excludes electrical changes - none were requested." });
  }

  // --- Plumbing (gated on common plumbing_changes, tiered) ----------------
  const plumbingTier = rawString(input, "whole_home_plumbing_scope");
  if (input.workType.plumbingChanges === "yes" || plumbingTier) {
    const plumbingItemId = plumbingTier ? PLUMBING_ITEM_BY_TIER[plumbingTier] : "whole_home_plumbing_minor";
    const plumbing = plumbingItemId ? applyItem(catalog, plumbingItemId, { finishLevel, regionalMultiplier }) : null;
    if (plumbing) {
      applied.push(plumbing);
      if (!plumbingTier) {
        assumptions.push({ code: "whole_home_plumbing_defaulted", message: 'Plumbing changes were indicated but no scope tier was selected - defaulted to "minor updates" for planning purposes.' });
      }
    } else if (input.workType.plumbingChanges === "yes") {
      warnings.push({ code: "whole_home_plumbing_no_applicable_pricing", message: "Plumbing changes were indicated, but no applicable plumbing pricing could be applied for this configuration - consult a plumber directly for this scope.", severity: "caution" });
    }
  } else {
    exclusions.push({ code: "excludes_whole_home_plumbing", message: "Excludes plumbing changes - none were requested." });
  }

  // --- HVAC (tiered, not gated on a common flag - no shared hvac question) ---
  const hvacScope = rawString(input, "whole_home_hvac_scope");
  const hvacItemId = hvacScope ? HVAC_ITEM_BY_SCOPE[hvacScope] : null;
  if (hvacItemId) {
    const hvac = applyItem(catalog, hvacItemId, { finishLevel, regionalMultiplier });
    if (hvac) applied.push(hvac);
  } else {
    exclusions.push({
      code: "excludes_whole_home_hvac",
      message: hvacScope === "keep_existing" ? "Existing heating/cooling system is being kept - no HVAC cost is included." : "No HVAC selection was provided - excluded from this estimate."
    });
  }

  // --- Exterior work (tiered, per total sqft as an envelope-size proxy) ---
  const exteriorScope = rawString(input, "whole_home_exterior_scope");
  const exteriorItemId = exteriorScope ? EXTERIOR_ITEM_BY_SCOPE[exteriorScope] : null;
  if (exteriorItemId && sqft && sqft > 0) {
    const exterior = applyItem(catalog, exteriorItemId, { quantity: sqft, finishLevel, regionalMultiplier });
    if (exterior) applied.push(exterior);
  } else if (exteriorItemId) {
    // Exterior work was selected, but total home square footage is missing,
    // so the sqft-based proxy quantity can't be computed.
    exclusions.push({
      code: "excludes_whole_home_exterior",
      message: "Exterior work was requested, but total home square footage was not provided, so this cost could not be estimated."
    });
  } else {
    exclusions.push({
      code: "excludes_whole_home_exterior",
      message: exteriorScope === "none" ? "No exterior work was requested - excluded from this estimate." : "No exterior work selection was provided - excluded from this estimate."
    });
  }

  // --- Structural (percentage of base, gated on common structural_changes) ---
  if ((input.workType.structuralChanges === "yes" || input.workType.structuralChanges === "unknown") && findItem(catalog, "whole_home_structural_allowance")) {
    const structural = applyItem(catalog, "whole_home_structural_allowance", { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
    if (structural) {
      applied.push(structural);
      assumptions.push({
        code: "whole_home_structural_engineering_flag",
        message: "Structural changes were indicated (e.g. load-bearing walls, foundation work). This estimate includes only a placeholder structural allowance - a structural engineer's assessment and likely a structural permit are required before final pricing."
      });
    }
  } else if (input.workType.structuralChanges === "no") {
    exclusions.push({ code: "excludes_whole_home_structural", message: "Excludes structural work - none was requested." });
  }

  // --- Demolition (percentage of base) --------------------------------------
  if (input.workType.demolitionRequired === "yes" && findItem(catalog, "whole_home_demolition")) {
    const demolition = applyItem(catalog, "whole_home_demolition", { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
    if (demolition) applied.push(demolition);
  }

  // --- Accessibility --------------------------------------------------------
  if (input.accessibility.required === "yes") {
    const accessibility = applyItem(catalog, "whole_home_accessibility", { finishLevel, regionalMultiplier });
    if (accessibility) applied.push(accessibility);
  }

  // --- Design services / permit allowance (percentage of base, scales with
  //     project size - see pricing-data doc comment on why this differs
  //     from the flat allowance kitchen/bathroom/basement use) -------------
  if (input.designAndPermits.needsDesignServices === "yes" || input.designAndPermits.permitLikelyRequired !== "no") {
    const permitDesign = applyItem(catalog, "whole_home_permit_design", { regionalMultiplier, baseAmountForPercentage: baseTypicalForPercentage });
    if (permitDesign) applied.push(permitDesign);
  }

  // --- Permit likely-required nudge for high-scope combinations -----------
  // A whole-home project with structural, a full rewire, or a full
  // re-pipe almost always needs a permit even if the user hasn't
  // confirmed one - surfaced as an assumption to confirm, not silently
  // assumed into the price.
  const highScopeSelected =
    input.workType.structuralChanges === "yes" ||
    electricalTier === "full_rewire_panel_upgrade" ||
    plumbingTier === "full_repipe";
  if (highScopeSelected && input.designAndPermits.permitLikelyRequired !== "yes") {
    assumptions.push({
      code: "whole_home_permit_very_likely",
      message: "Given the scope selected (structural changes and/or a full electrical/plumbing overhaul), a permit is very likely required even though this wasn't confirmed - confirm specific permit requirements with your municipality before finalizing budget."
    });
  }

  // --- Special handling: standing whole-home disclosures --------------------
  if (bedroomsCount === undefined) {
    assumptions.push({
      code: "whole_home_bedrooms_not_provided",
      message: "Number of bedrooms was not provided - this doesn't directly affect pricing today, but helps validate the overall project scope against square footage."
    });
  }
  if (floorsCount === undefined) {
    assumptions.push({
      code: "whole_home_floors_not_provided",
      message: "Number of floors was not provided - assumed a single floor for any calculation that depends on it (e.g. estimating basement square footage)."
    });
  }

  const output: ProjectCalculationOutput = { applied, assumptions, exclusions, anchorMissing, warnings };
  return output;
};
